/**
 * PATH: packages/ui/src/components/sidebar/CollapsedHubsPortal.tsx
 * PURPOSE: Portal dropdown for hub list when sidebar is collapsed
 */

import React from 'react'
import { createPortal } from 'react-dom'
import { ExternalLink as ExternalLinkIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { ExternalLink } from '../types'

interface CollapsedHubsPortalProps {
  open: boolean
  position: { top: number; left: number }
  title: 'Switch Hub' | 'All Hubs'
  externalLinks: (ExternalLink & { status?: 'production' | 'development' | 'planned' })[]
  dropdownRef: React.RefObject<HTMLDivElement | null>
  onClose: () => void
  onHubClick?: (hubId: string, href: string) => void
}

export function CollapsedHubsPortal({
  open,
  position,
  title,
  externalLinks,
  dropdownRef,
  onClose,
  onHubClick,
}: CollapsedHubsPortalProps) {
  if (!open || externalLinks.length === 0) return null

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed w-56 bg-popover border border-border rounded-xl shadow-2xl py-2 animate-in slide-in-from-left-2 duration-150 max-h-[calc(100vh-4rem)] overflow-y-auto"
      style={{ zIndex: 9999, top: position.top, left: position.left }}
    >
      <div className="px-3 pb-2 border-b border-border">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </p>
      </div>
      <div className="py-1 max-h-80 overflow-y-auto">
        {externalLinks.map((link) => (
          <a
            key={link.id || link.href}
            href={link.href}
            onClick={(e) => {
              if (link.status === 'planned') {
                e.preventDefault()
                onClose()
                return
              }
              if (onHubClick && link.id) {
                e.preventDefault()
                onHubClick(link.id, link.href)
                onClose()
                return
              }
              // Let browser handle normal <a href> navigation — no preventDefault, no window.open
              onClose()
            }}
            className={cn(
              'flex items-center gap-2.5 px-3 py-2 text-[13px] transition-colors group',
              link.status === 'planned'
                ? 'text-muted-foreground hover:bg-muted cursor-not-allowed'
                : link.status === 'development'
                  ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                  : 'text-muted-foreground hover:bg-muted'
            )}
          >
            <div
              className={cn(
                'w-7 h-7 rounded-lg flex items-center justify-center transition-colors',
                link.status === 'development'
                  ? 'bg-amber-100 dark:bg-amber-900/30 group-hover:bg-amber-200 dark:group-hover:bg-amber-800/40'
                  : link.status === 'planned'
                    ? 'bg-muted group-hover:bg-accent'
                    : 'bg-muted group-hover:bg-teal-50 dark:group-hover:bg-teal-900/30'
              )}
            >
              <link.icon
                className={cn(
                  'w-4 h-4',
                  link.status === 'development'
                    ? 'text-amber-600 dark:text-amber-400'
                    : link.status === 'planned'
                      ? 'text-muted-foreground'
                      : 'text-muted-foreground group-hover:text-teal-600 dark:group-hover:text-teal-400'
                )}
              />
            </div>
            <span className="flex-1 truncate">{link.label}</span>
            {link.status === 'development' && (
              <span className="px-1 py-0.5 text-[8px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded">
                DEV
              </span>
            )}
            {link.status === 'planned' && (
              <span className="px-1 py-0.5 text-[8px] font-medium bg-muted text-muted-foreground rounded">
                SOON
              </span>
            )}
          </a>
        ))}
      </div>
    </div>,
    document.body
  )
}
